import { useCallback, useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import {
  cancelMemberBingoCellComplete,
  cancelMemberMissionComplete,
  completeMemberBingoCell,
  completeMemberMission,
  getCompletedBingos,
  getCompletedMissions,
  getInProgressBingos,
  getInProgressMissions,
  getMemberBingoDetail,
  getMemberMissionDetail,
  getPubMissionMemberStatsList,
  SellerMemberBingoItemResponse,
  SellerMemberBingoDetailResponse,
  SellerMemberMissionItemResponse,
  SellerMemberMissionDetailResponse,
  SellerPubMissionMemberStatsItemResponse,
  updateMemberBingoCellProgress,
  updateMemberMissionProgress,
} from "../../../api/pub-mission-member";
import { pubState } from "../../../recoil/pub.atom";

const MEMBER_LIMIT = 10;
const LIST_LIMIT = 5;

interface ListFilter {
  name: string;
  startDate: string;
  endDate: string;
  page: number;
}

const emptyFilter = (): ListFilter => ({
  name: "",
  startDate: "",
  endDate: "",
  page: 1,
});

export const useMemberProgressPage = () => {
  const pub = useRecoilValue(pubState);
  const pubId = pub?.id ?? 0;

  const [memberSearch, setMemberSearch] = useState("");
  const [memberPage, setMemberPage] = useState(1);
  const [memberTotal, setMemberTotal] = useState(0);
  const [members, setMembers] = useState<
    SellerPubMissionMemberStatsItemResponse[]
  >([]);
  const [selectedMember, setSelectedMember] =
    useState<SellerPubMissionMemberStatsItemResponse | null>(null);

  const [inProgressMissionFilter, setInProgressMissionFilter] =
    useState<ListFilter>(emptyFilter());
  const [inProgressMissionTotal, setInProgressMissionTotal] = useState(0);
  const [inProgressMissions, setInProgressMissions] = useState<
    SellerMemberMissionItemResponse[]
  >([]);

  const [completedMissionFilter, setCompletedMissionFilter] =
    useState<ListFilter>(emptyFilter());
  const [completedMissionTotal, setCompletedMissionTotal] = useState(0);
  const [completedMissions, setCompletedMissions] = useState<
    SellerMemberMissionItemResponse[]
  >([]);

  const [inProgressBingoFilter, setInProgressBingoFilter] =
    useState<ListFilter>(emptyFilter());
  const [inProgressBingoTotal, setInProgressBingoTotal] = useState(0);
  const [inProgressBingos, setInProgressBingos] = useState<
    SellerMemberBingoItemResponse[]
  >([]);

  const [completedBingoFilter, setCompletedBingoFilter] =
    useState<ListFilter>(emptyFilter());
  const [completedBingoTotal, setCompletedBingoTotal] = useState(0);
  const [completedBingos, setCompletedBingos] = useState<
    SellerMemberBingoItemResponse[]
  >([]);

  const [selectedMissionRowId, setSelectedMissionRowId] = useState<
    number | null
  >(null);
  const [selectedMissionId, setSelectedMissionId] = useState<number | null>(
    null,
  );
  const [selectedMemberProgressId, setSelectedMemberProgressId] = useState<
    number | null
  >(null);
  const [missionDetail, setMissionDetail] =
    useState<SellerMemberMissionDetailResponse | null>(null);
  const [missionDetailLoading, setMissionDetailLoading] = useState(false);
  const [missionProgressInput, setMissionProgressInput] = useState("");

  const [selectedBingoId, setSelectedBingoId] = useState<number | null>(null);
  const [bingoDetail, setBingoDetail] =
    useState<SellerMemberBingoDetailResponse | null>(null);
  const [bingoDetailLoading, setBingoDetailLoading] = useState(false);
  const [selectedCellId, setSelectedCellId] = useState<number | null>(null);
  const [cellProgressInput, setCellProgressInput] = useState("");

  const fetchMembers = useCallback(async () => {
    if (!pubId) return;
    const res = await getPubMissionMemberStatsList({
      pubId,
      page: memberPage,
      limit: MEMBER_LIMIT,
      nickname: memberSearch || undefined,
    });
    setMembers(res.list);
    setMemberTotal(res.total);
  }, [pubId, memberPage, memberSearch]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const fetchMemberLists = useCallback(async () => {
    if (!selectedMember || !pubId) return;

    const base = {
      memberId: selectedMember.memberId,
      pubId,
      limit: LIST_LIMIT,
    };

    const [inMission, doneMission, inBingo, doneBingo] = await Promise.all([
      getInProgressMissions({
        ...base,
        page: inProgressMissionFilter.page,
        name: inProgressMissionFilter.name || undefined,
        startDate: inProgressMissionFilter.startDate || undefined,
        endDate: inProgressMissionFilter.endDate || undefined,
      }),
      getCompletedMissions({
        ...base,
        page: completedMissionFilter.page,
        name: completedMissionFilter.name || undefined,
        startDate: completedMissionFilter.startDate || undefined,
        endDate: completedMissionFilter.endDate || undefined,
      }),
      getInProgressBingos({
        ...base,
        page: inProgressBingoFilter.page,
        name: inProgressBingoFilter.name || undefined,
        startDate: inProgressBingoFilter.startDate || undefined,
        endDate: inProgressBingoFilter.endDate || undefined,
      }),
      getCompletedBingos({
        ...base,
        page: completedBingoFilter.page,
        name: completedBingoFilter.name || undefined,
        startDate: completedBingoFilter.startDate || undefined,
        endDate: completedBingoFilter.endDate || undefined,
      }),
    ]);

    setInProgressMissions(inMission.list);
    setInProgressMissionTotal(inMission.total);
    setCompletedMissions(doneMission.list);
    setCompletedMissionTotal(doneMission.total);
    setInProgressBingos(inBingo.list);
    setInProgressBingoTotal(inBingo.total);
    setCompletedBingos(doneBingo.list);
    setCompletedBingoTotal(doneBingo.total);
  }, [
    selectedMember,
    pubId,
    inProgressMissionFilter,
    completedMissionFilter,
    inProgressBingoFilter,
    completedBingoFilter,
  ]);

  useEffect(() => {
    fetchMemberLists();
  }, [fetchMemberLists]);

  const loadMissionDetail = useCallback(
    async (missionId: number, memberProgressId?: number | null) => {
      if (!selectedMember || !pubId) return;
      setMissionDetailLoading(true);
      try {
        const detail = await getMemberMissionDetail(missionId, {
          memberId: selectedMember.memberId,
          pubId,
          memberProgressId: memberProgressId || undefined,
        });
        setMissionDetail(detail);
        setMissionProgressInput(String(detail.currentCount));
      } catch (error: any) {
        alert(error?.message || "미션 정보를 불러오지 못했습니다.");
        setSelectedMissionRowId(null);
        setSelectedMissionId(null);
        setSelectedMemberProgressId(null);
        setMissionDetail(null);
      } finally {
        setMissionDetailLoading(false);
      }
    },
    [selectedMember, pubId],
  );

  const loadBingoDetail = useCallback(
    async (bingoId: number) => {
      if (!selectedMember || !pubId) return;
      setBingoDetailLoading(true);
      try {
        const detail = await getMemberBingoDetail(bingoId, {
          memberId: selectedMember.memberId,
          pubId,
        });
        setBingoDetail(detail);
        const firstCell = detail.cells[0];
        setSelectedCellId(firstCell?.id ?? null);
        if (firstCell) {
          setCellProgressInput(String(firstCell.currentCount));
        }
      } catch (error: any) {
        alert(error?.message || "빙고 정보를 불러오지 못했습니다.");
        setSelectedBingoId(null);
        setBingoDetail(null);
      } finally {
        setBingoDetailLoading(false);
      }
    },
    [selectedMember, pubId],
  );

  useEffect(() => {
    if (selectedMissionId) {
      loadMissionDetail(selectedMissionId, selectedMemberProgressId);
    } else {
      setMissionDetail(null);
    }
  }, [selectedMissionId, selectedMemberProgressId, loadMissionDetail]);

  useEffect(() => {
    if (selectedBingoId) {
      loadBingoDetail(selectedBingoId);
    } else {
      setBingoDetail(null);
      setSelectedCellId(null);
    }
  }, [selectedBingoId, loadBingoDetail]);

  const selectMember = useCallback(
    (member: SellerPubMissionMemberStatsItemResponse) => {
      setSelectedMember(member);
      setSelectedMissionRowId(null);
      setSelectedMissionId(null);
      setSelectedMemberProgressId(null);
      setSelectedBingoId(null);
      setInProgressMissionFilter(emptyFilter());
      setCompletedMissionFilter(emptyFilter());
      setInProgressBingoFilter(emptyFilter());
      setCompletedBingoFilter(emptyFilter());
    },
    [],
  );

  const selectMission = useCallback(
    (item: SellerMemberMissionItemResponse) => {
      setSelectedMissionRowId(item.id);
      setSelectedMissionId(item.missionId);
      setSelectedMemberProgressId(item.memberProgressId);
      setSelectedBingoId(null);
    },
    [],
  );

  const selectBingo = useCallback((bingoId: number) => {
    setSelectedBingoId(bingoId);
    setSelectedMissionRowId(null);
    setSelectedMissionId(null);
    setSelectedMemberProgressId(null);
  }, []);

  const refreshAll = useCallback(async () => {
    await fetchMembers();
    await fetchMemberLists();
    if (selectedMissionId) {
      await loadMissionDetail(selectedMissionId, selectedMemberProgressId);
    }
    if (selectedBingoId) await loadBingoDetail(selectedBingoId);
  }, [
    fetchMembers,
    fetchMemberLists,
    loadMissionDetail,
    loadBingoDetail,
    selectedMissionId,
    selectedMemberProgressId,
    selectedBingoId,
  ]);

  const memberAction = useCallback(
    async (action: () => Promise<unknown>) => {
      try {
        await action();
        await refreshAll();
      } catch (error: any) {
        alert(error?.message || "처리에 실패했습니다.");
      }
    },
    [refreshAll],
  );

  const completeMission = () => {
    if (!selectedMember || !selectedMissionId) return;
    memberAction(() =>
      completeMemberMission(selectedMissionId, {
        memberId: selectedMember.memberId,
        pubId,
        memberProgressId: selectedMemberProgressId || undefined,
      }),
    );
  };

  const cancelMissionComplete = (memo?: string) => {
    if (!selectedMember || !selectedMissionId) return;
    memberAction(() =>
      cancelMemberMissionComplete(selectedMissionId, {
        memberId: selectedMember.memberId,
        pubId,
        memberProgressId: selectedMemberProgressId || undefined,
        memo: memo?.trim() || undefined,
      }),
    );
  };

  const saveMissionProgress = (currentCount: number) => {
    if (!selectedMember || !selectedMissionId) return;
    memberAction(() =>
      updateMemberMissionProgress(selectedMissionId, {
        memberId: selectedMember.memberId,
        pubId,
        memberProgressId: selectedMemberProgressId || undefined,
        currentCount,
      }),
    );
  };

  const completeBingoCell = (cellId: number) => {
    if (!selectedMember || !selectedBingoId) return;
    memberAction(() =>
      completeMemberBingoCell(selectedBingoId, cellId, {
        memberId: selectedMember.memberId,
        pubId,
      }),
    );
  };

  const cancelBingoCell = (cellId: number, memo?: string) => {
    if (!selectedMember || !selectedBingoId) return;
    memberAction(() =>
      cancelMemberBingoCellComplete(selectedBingoId, cellId, {
        memberId: selectedMember.memberId,
        pubId,
        memo: memo?.trim() || undefined,
      }),
    );
  };

  const saveCellProgress = (cellId: number, currentCount: number) => {
    if (!selectedMember || !selectedBingoId) return;
    memberAction(() =>
      updateMemberBingoCellProgress(selectedBingoId, cellId, {
        memberId: selectedMember.memberId,
        pubId,
        currentCount,
      }),
    );
  };

  const selectedCell =
    bingoDetail?.cells.find((cell) => cell.id === selectedCellId) ?? null;

  const selectCell = (cellId: number, currentCount: number) => {
    setSelectedCellId(cellId);
    setCellProgressInput(String(currentCount));
  };

  return {
    pubId,
    memberSearch,
    setMemberSearch,
    memberPage,
    setMemberPage,
    memberTotal,
    memberLimit: MEMBER_LIMIT,
    members,
    fetchMembers,
    selectedMember,
    selectMember,
    inProgressMissions,
    inProgressMissionTotal,
    inProgressMissionFilter,
    setInProgressMissionFilter,
    listLimit: LIST_LIMIT,
    completedMissions,
    completedMissionTotal,
    completedMissionFilter,
    setCompletedMissionFilter,
    inProgressBingos,
    inProgressBingoTotal,
    inProgressBingoFilter,
    setInProgressBingoFilter,
    completedBingos,
    completedBingoTotal,
    completedBingoFilter,
    setCompletedBingoFilter,
    selectedMissionRowId,
    selectedMissionId,
    selectMission,
    missionDetail,
    missionDetailLoading,
    missionProgressInput,
    setMissionProgressInput,
    completeMission,
    cancelMissionComplete,
    saveMissionProgress,
    selectedBingoId,
    selectBingo,
    bingoDetail,
    bingoDetailLoading,
    selectedCell,
    selectCell,
    cellProgressInput,
    setCellProgressInput,
    completeBingoCell,
    cancelBingoCell,
    saveCellProgress,
  };
};
